import React, { useRef, useState } from 'react'
import clsx from 'clsx'
import styles from './styles/CodeInput.module.css'

interface CodeInputProps {
  id?: string
  name?: string
  value?: string
  length?: number
  disabled?: boolean
  isInvalid?: boolean
  autoFocus?: boolean
  ariaLabel?: string
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
}

export const CodeInput: React.FC<CodeInputProps> = ({
  id,
  name,
  value = '',
  length = 6,
  disabled = false,
  isInvalid = false,
  autoFocus = false,
  ariaLabel,
  onChange,
  onBlur,
}) => {
  const [focado, setFocado] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const digitos = value.slice(0, length)
  const posicaoAtual = focado ? Math.min(digitos.length, length - 1) : -1

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!onChange) return

    const limpo = event.target.value.replace(/\D/g, '').slice(0, length)
    const eventoNormalizado = {
      target: { name, value: limpo },
    } as unknown as React.ChangeEvent<HTMLInputElement>
    onChange(eventoNormalizado)
  }

  const mantemCursorNoFim = () => {
    const campo = inputRef.current
    if (!campo) return

    const fim = campo.value.length
    if (campo.selectionStart !== fim || campo.selectionEnd !== fim) {
      campo.setSelectionRange(fim, fim)
    }
  }

  const classeCaixa = (indice: number) =>
    clsx(
      styles.box,
      'flex-fill h-55px d-flex align-items-center justify-content-center border rounded bg-body text-gray-900 lh-1 fw-bold fs-1',
      {
        [styles.boxActive]: indice === posicaoAtual,
        'border-primary': indice === posicaoAtual,
        'border-danger': isInvalid && indice !== posicaoAtual,
        'border-gray-500': !isInvalid && indice !== posicaoAtual && Boolean(digitos[indice]),
        'border-gray-300': !isInvalid && indice !== posicaoAtual && !digitos[indice],
        'bg-light text-gray-600 opacity-75': disabled,
      }
    )

  return (
    <div className='position-relative w-100'>
      <div className='d-flex gap-2 w-100' aria-hidden='true'>
        {Array.from({ length }, (_, indice) => (
          <div key={indice} className={classeCaixa(indice)}>
            {digitos[indice] ?? ''}
          </div>
        ))}
      </div>
      <input
        ref={inputRef}
        id={id}
        name={name}
        type='text'
        className={clsx(
          styles.field,
          'position-absolute top-0 start-0 w-100 h-100 p-0 m-0 border-0 bg-transparent'
        )}
        value={digitos}
        disabled={disabled}
        autoFocus={autoFocus}
        inputMode='numeric'
        autoComplete='one-time-code'
        aria-label={ariaLabel}
        aria-invalid={isInvalid}
        onChange={handleChange}
        onSelect={mantemCursorNoFim}
        onFocus={() => setFocado(true)}
        onBlur={(event) => {
          setFocado(false)
          onBlur?.(event)
        }}
      />
    </div>
  )
}
